import React from 'react';
import { Link, Typography, TypographyProps, useTheme, Box } from '@mui/material';
import { convertLinksToElements } from '../utils/markdownUtils';

interface LinkifyTextProps extends Omit<TypographyProps, 'children'> {
  text: string;
  targetBlank?: boolean;
  linkClassName?: string;
}

/**
 * Component that renders text with automatic link detection
 * This is an alternative to using ReactMarkdown for just link detection
 * Preserves paragraphs, line breaks, and text formatting
 */
const LinkifyText: React.FC<LinkifyTextProps> = ({
  text,
  targetBlank = true,
  linkClassName = '',
  ...typographyProps
}) => {
  const theme = useTheme();
  
  // Split text into paragraphs first (double line breaks)
  const paragraphs = text.split(/\n{2,}/);
  
  return (
    <>
      {paragraphs.map((paragraph, pIndex) => {
        // Process each paragraph for single line breaks and links
        const lines = paragraph.split(/\n/);
        
        return (
          <Box key={`p-${pIndex}`} mb={pIndex < paragraphs.length - 1 ? 2 : 0}>
            {lines.map((line, lIndex) => {
              const elements = convertLinksToElements(line, targetBlank, linkClassName);
              
              return (
                <React.Fragment key={`line-${pIndex}-${lIndex}`}>
                  <Typography 
                    component="span" 
                    display="block"
                    {...typographyProps}
                    sx={{
                      ...(typographyProps.sx || {}),
                      // Don't add margin to last line in a paragraph
                      mb: lIndex < lines.length - 1 ? 0.5 : 0
                    }}
                  >
                    {elements.map((part, index) => {
                      if (typeof part === 'string') {
                        return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
                      } else {
                        // This is a link
                        return (
                          <Link
                            key={part.id}
                            href={part.url}
                            target={targetBlank ? '_blank' : '_self'}
                            rel={targetBlank ? 'noopener noreferrer' : ''}
                            className={linkClassName}
                            sx={{
                              color: theme.palette.primary.main,
                              textDecoration: 'underline',
                              '&:hover': {
                                color: theme.palette.primary.dark,
                              },
                            }}
                          >
                            {part.text}
                          </Link>
                        );
                      }
                    })}
                  </Typography>
                </React.Fragment>
              );
            })}
          </Box>
        );
      })}
    </>
  );
};

export default LinkifyText;